import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogOut, Mail, Calendar, Briefcase, Shield } from "lucide-react";
import { toast } from "sonner";

export function AdminLoginPage() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await login(form.email, form.password);
      nav("/admin");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-[70vh] flex items-center justify-center px-5 py-20">
      <div className="w-full max-w-sm border border-slate-800 bg-slate-950 p-8">
        <div className="flex items-center gap-3 mb-8">
          <Shield size={22} className="text-amber-400" />
          <div>
            <div className="font-mono-spec text-[10px] text-amber-400">OLIVINE</div>
            <div className="font-display font-bold text-white text-lg">{t("admin.login_title")}</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-xs font-mono-spec text-slate-400">EMAIL</span>
            <input
              data-testid="admin-login-email"
              type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 outline-none text-white p-3 rounded-sm text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-mono-spec text-slate-400">PASSWORD</span>
            <input
              data-testid="admin-login-password"
              type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 outline-none text-white p-3 rounded-sm text-sm"
            />
          </label>
          {err && <div className="text-sm text-red-400" data-testid="admin-login-error">{err}</div>}
          <button
            type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full bg-amber-500 text-slate-950 font-bold px-5 py-3 hover:bg-amber-400 rounded-sm disabled:opacity-60"
          >
            {loading ? "Signing in…" : t("admin.login_cta")}
          </button>
        </form>
      </div>
    </div>
  );
}

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [data, setData] = useState({ contacts: [], demos: [], applications: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const [c, d, a] = await Promise.all([
          api.get("/admin/contacts"),
          api.get("/admin/demo-requests"),
          api.get("/admin/applications"),
        ]);
        setData({ contacts: c.data, demos: d.data, applications: a.data });
      } catch (e) {
        toast.error("Failed to load data");
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user === null) return <Navigate to="/admin/login" replace />;

  const TABS = [
    { key: "contacts", label: t("admin.tabs.contacts"), icon: Mail, count: data.contacts.length },
    { key: "demos", label: t("admin.tabs.demos"), icon: Calendar, count: data.demos.length },
    { key: "applications", label: t("admin.tabs.applications"), icon: Briefcase, count: data.applications.length },
  ];

  const handleLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen pb-24">
      <div className="border-b border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex items-center justify-between">
          <div>
            <div className="font-mono-spec text-[10px] text-amber-400">ADMIN CONSOLE</div>
            <h1 className="mt-1 font-display text-2xl font-bold text-white">{t("admin.title")}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-slate-400 hidden sm:block">{user.email}</div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-700 text-slate-200 hover:border-amber-500 hover:text-amber-400 rounded-sm text-sm"
            >
              <LogOut size={14} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-8">
        <div className="flex gap-2 border-b border-slate-800">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              data-testid={`admin-tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === tb.key ? "text-amber-400 border-amber-400" : "text-slate-400 border-transparent hover:text-white"
              }`}
            >
              <tb.icon size={14} /> {tb.label} <span className="text-xs text-slate-500">({tb.count})</span>
            </button>
          ))}
        </div>

        <div className="pt-8" data-testid={`admin-tab-content-${tab}`}>
          {loading ? <div className="text-slate-400">Loading…</div> : (
            <>
              {tab === "contacts" && <ContactsTable items={data.contacts} />}
              {tab === "demos" && <DemosTable items={data.demos} />}
              {tab === "applications" && <AppsTable items={data.applications} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactsTable({ items }) {
  if (!items.length) return <div className="text-slate-400 text-sm p-8 border border-slate-800 bg-slate-950">No contacts yet.</div>;
  return (
    <div className="border border-slate-800 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-950 border-b border-slate-800">
          <tr className="text-left font-mono-spec text-[10px] text-slate-400">
            <th className="p-3">DATE</th><th className="p-3">NAME</th><th className="p-3">EMAIL</th><th className="p-3">COMPANY</th><th className="p-3">SUBJECT</th><th className="p-3">MESSAGE</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-slate-800 bg-slate-950 hover:bg-slate-900" data-testid={`contact-row-${c.id}`}>
              <td className="p-3 text-slate-400 whitespace-nowrap">{(c.created_at || "").slice(0, 10)}</td>
              <td className="p-3 text-white">{c.name}</td>
              <td className="p-3 text-slate-300">{c.email}</td>
              <td className="p-3 text-slate-300">{c.company || "—"}</td>
              <td className="p-3 text-slate-300">{c.subject || "—"}</td>
              <td className="p-3 text-slate-400 max-w-sm truncate" title={c.message}>{c.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DemosTable({ items }) {
  if (!items.length) return <div className="text-slate-400 text-sm p-8 border border-slate-800 bg-slate-950">No demo requests yet.</div>;
  return (
    <div className="border border-slate-800 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-950 border-b border-slate-800">
          <tr className="text-left font-mono-spec text-[10px] text-slate-400">
            <th className="p-3">DATE</th><th className="p-3">NAME</th><th className="p-3">COMPANY</th><th className="p-3">INDUSTRY</th><th className="p-3">PRODUCT</th><th className="p-3">PREFERRED</th><th className="p-3">EMAIL</th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id} className="border-b border-slate-800 bg-slate-950 hover:bg-slate-900" data-testid={`demo-row-${d.id}`}>
              <td className="p-3 text-slate-400 whitespace-nowrap">{(d.created_at || "").slice(0, 10)}</td>
              <td className="p-3 text-white">{d.name}</td>
              <td className="p-3 text-slate-300">{d.company}</td>
              <td className="p-3 text-slate-300">{d.industry || "—"}</td>
              <td className="p-3 text-slate-300">{d.product_interest || "—"}</td>
              <td className="p-3 text-slate-300">{d.preferred_date || "—"}</td>
              <td className="p-3 text-slate-300">{d.email}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AppsTable({ items }) {
  if (!items.length) return <div className="text-slate-400 text-sm p-8 border border-slate-800 bg-slate-950">No applications yet.</div>;
  return (
    <div className="border border-slate-800 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-950 border-b border-slate-800">
          <tr className="text-left font-mono-spec text-[10px] text-slate-400">
            <th className="p-3">DATE</th><th className="p-3">POSITION</th><th className="p-3">NAME</th><th className="p-3">EMAIL</th><th className="p-3">PHONE</th><th className="p-3">RESUME</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-b border-slate-800 bg-slate-950 hover:bg-slate-900" data-testid={`app-row-${a.id}`}>
              <td className="p-3 text-slate-400 whitespace-nowrap">{(a.created_at || "").slice(0, 10)}</td>
              <td className="p-3 text-white">{a.position_id}</td>
              <td className="p-3 text-slate-300">{a.name}</td>
              <td className="p-3 text-slate-300">{a.email}</td>
              <td className="p-3 text-slate-300">{a.phone || "—"}</td>
              <td className="p-3 text-blue-400 truncate max-w-xs">
                {a.resume_url ? <a href={a.resume_url} target="_blank" rel="noreferrer" className="underline">link</a> : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
